import React, { useMemo, useState } from 'react';
import { Box, Avatar } from '@mui/material';
import ImageIcon from '@mui/icons-material/Image';
import { getStaticFileUrl, guessOtaStaticPath, resolveChannelImageUrl } from '../../utils/staticUtils';

/**
 * ChannelImage - Renders channel image with fallback to Material Design icon
 *
 * @param {Object} props
 * @param {Object} channel - Channel object with {id, name, image_path}
 * @param {number} size - Size of the image in pixels
 * @param {boolean} isPMS - Whether this is PMS (uses pms.png)
 */
const ChannelImage = ({ channel, size = 32, isPMS = false }) => {
  const [imageError, setImageError] = useState(false);
  const [useNameFallback, setUseNameFallback] = useState(false);

  const primarySrc = useMemo(
    () => resolveChannelImageUrl(channel, { isPMS }),
    [channel, isPMS],
  );

  const fallbackSrc = useMemo(() => {
    if (isPMS || channel?.id === 'pms') return getStaticFileUrl('ota/pms.png');
    const guessed = guessOtaStaticPath(channel?.name);
    return guessed ? getStaticFileUrl(guessed) : null;
  }, [channel?.id, channel?.name, isPMS]);

  const imageSrc = useNameFallback ? fallbackSrc : primarySrc;

  if (imageError || !imageSrc) {
    return (
      <Avatar
        sx={{
          width: size,
          height: size,
          bgcolor: 'action.disabledBackground',
          color: 'action.disabled',
        }}
      >
        <ImageIcon sx={{ fontSize: size * 0.6 }} />
      </Avatar>
    );
  }

  return (
    <Box
      component="img"
      src={imageSrc}
      alt={isPMS ? 'PMS' : (channel?.name || 'Channel')}
      sx={{
        width: size,
        height: size,
        objectFit: 'contain',
        flexShrink: 0,
        borderRadius: '4px',
      }}
      onError={() => {
        if (!useNameFallback && fallbackSrc && fallbackSrc !== imageSrc) {
          setUseNameFallback(true);
          return;
        }
        setImageError(true);
      }}
    />
  );
};

export default ChannelImage;
